/**
 * Utility functions for extracting authentication tokens from URL parameters
 */

import { getLogger } from '@/lib/logger';
import type {
  PropertyExtractionResult,
  ValidationResult,
  TokenExtractionResult,
  PlatformExtractionResult,
  ParamExtractionResult,
} from '@/types';

const logger = getLogger({ scope: 'utils.url-token-extractor' });

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Extract property from URL search parameters
 * Supports multiple parameter names for flexibility
 */
function extractPropertyFromUrl(
  paramNames: string[],
  url: string = window.location.href
): PropertyExtractionResult {
  try {
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;

    // Try each parameter name
    for (const paramName of paramNames) {
      const property = searchParams.get(paramName);
      if (property && property.trim()) {
        return { target: property.trim(), error: null };
      }
    }

    return {
      target: null,
      error: `No token found in URL parameters. Checked: ${paramNames.join(', ')}`,
    };
  } catch (error) {
    return {
      target: null,
      error: `Failed to parse URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Validate that a token meets basic requirements
 */
function validateToken(token: string): ValidationResult {
  if (!token || typeof token !== 'string') {
    return { isValid: false, error: 'Token must be a non-empty string' };
  }

  const trimmedToken = token.trim();

  if (trimmedToken.length === 0) {
    return { isValid: false, error: 'Token cannot be empty or whitespace only' };
  }

  if (trimmedToken.length < 10) {
    return { isValid: false, error: 'Token appears to be too short' };
  }

  // Basic format validation - tokens should not contain spaces
  if (trimmedToken.includes(' ')) {
    return { isValid: false, error: 'Token should not contain spaces' };
  }

  return { isValid: true };
}

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Clean URL by removing token parameters after extraction.
 * Skipped on the verify route, which owns the `token` param itself.
 */
function cleanUrlAfterTokenExtraction(paramNames: string[] = ['auth', 'authToken']): void {
  try {
    const url = new URL(window.location.href);

    let hasChanges = false;

    paramNames.forEach((paramName) => {
      if (url.searchParams.has(paramName)) {
        url.searchParams.delete(paramName);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      const cleanUrl = url.pathname + (url.search || '') + (url.hash || '');
      window.history.replaceState({}, '', cleanUrl);
    }
  } catch (error) {
    logger.warn('Failed to clean URL after token extraction', { error });
  }
}

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Complete token extraction workflow
 * Extracts, validates, and cleans URL in one operation
 */
export function extractAndValidateTokenFromUrl(): TokenExtractionResult {
  const res = extractAndValidateParamFromUrl(['auth', 'authToken'], validateToken, [
    'auth',
    'authToken',
  ]);
  return { token: res.value, isValid: res.isValid, error: res.error };
}

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Complete platform extraction workflow
 * Extracts, validates, and cleans URL in one operation
 * @param validator - Platform validation function (should come from service layer)
 */
export function extractAndValidatePlatformFromUrl(
  validator?: (platform: string) => ValidationResult
): PlatformExtractionResult {
  // Default to basic validation if no validator provided
  const defaultValidator = (platform: string): ValidationResult => {
    if (!platform || typeof platform !== 'string' || platform.trim().length === 0) {
      return { isValid: false, error: 'Platform must be a non-empty string' };
    }
    return { isValid: true };
  };

  const res = extractAndValidateParamFromUrl(['platform'], validator || defaultValidator, [
    'platform',
  ]);
  return { platform: res.value, isValid: res.isValid, error: res.error };
}

/**
 * @deprecated Remove this when login link flow is fully implemented
 *
 * Generic extraction + validation workflow for any URL parameter(s)
 * - paramNames: list of parameter keys to try in order
 * - validator: function to validate the extracted value
 * - paramsToClean: optional list of parameters to remove from URL on success
 */
function extractAndValidateParamFromUrl(
  paramNames: string[],
  validator: (value: string) => ValidationResult,
  paramsToClean: string[] = paramNames
): ParamExtractionResult {
  const extraction = extractPropertyFromUrl(paramNames);

  if (!extraction.target) {
    return { value: null, isValid: false, error: extraction.error };
  }

  const validation = validator(extraction.target);

  if (!validation.isValid) {
    return { value: extraction.target, isValid: false, error: validation.error };
  }

  // Clean URL after successful extraction and validation
  cleanUrlAfterTokenExtraction(paramsToClean);

  return { value: extraction.target, isValid: true, error: null };
}
